import React, { useState, useEffect, useCallback } from "react";
import Hoc from "../HOC/Hoc";
import TopHeading from "../../utils/TopHeading/TopHeading";
import AnimatedPage from "../animatedPage/AnimatedPage";
import { Delete } from "@mui/icons-material";
import {
  Stack,
  TextField,
  Paper,
  Button,
  Grid,
  Snackbar,
  Alert,
  IconButton,
} from "@mui/material";
import CustomTooltip from "../../../src/utils/CustomTooltip";
import CustomDataTable from "../../views/CustomTable/CustomDataTable";
import ConfirmAlert from "../../utils/ConfirmAlert";
import { getWhitelistedIP, manageWhitelistIP } from "./ipWhiteList.api.js";
import moment from "moment";
import { useSelector } from "react-redux";

const SNACKBAR_DURATION = 5000;
const SNACKBAR_POSITION = { vertical: "bottom", horizontal: "right" };

const MESSAGES = {
  FETCH_ERROR: "Failed to fetch IP addresses.",
  FETCH_UNKNOWN: "Something went wrong while fetching IP addresses.",
  DELETE_SUCCESS: "IP address deleted successfully.",
  DELETE_ERROR: "Failed to delete IP address.",
  ADD_SUCCESS: "IP address added successfully.",
  ADD_ERROR: "Failed to add IP address.",
  IP_REQUIRED: "IP address is required.",
};

const buildColumns = (handleDelete) => [
  {
    name: "IP Address",
    selector: (row) => row.ipAddress ?? "--",
    sortable: true,
  },
  {
    name: "Created Date",
    selector: (row) =>
      row.createdAt ? moment(row.createdAt).format("DD-MM-YYYY") : "--",
    sortable: true,
  },
  {
    name: "Updated Date",
    selector: (row) =>
      row.updatedAt ? moment(row.updatedAt).format("DD-MM-YYYY") : "--",
    sortable: true,
  },
  {
    name: "Created By",
    selector: (row) => row.createdBy ?? "--",
    sortable: true,
    cell: (row) => (
      <CustomTooltip
        title={row.createdBy || "Email ID Not Found"}
        childrenText={row.createdBy || "--"}
        copiedText
      />
    ),
  },
  {
    name: "Updated By",
    selector: (row) => row.updatedBy ?? "--",
    cell: (row) => (
      <CustomTooltip
        title={row.updatedBy || "Email ID Not Found"}
        childrenText={row.updatedBy || "--"}
        copiedText
      />
    ),
    sortable: true,
  },
  {
    name: "Actions",
    sortable: false,
    cell: (row) => (
      <ConfirmAlert
        msg="Are you sure you want to delete?"
        onClickEvent={() => handleDelete(row)}
      >
        <IconButton color="error" aria-label="delete">
          <Delete />
        </IconButton>
      </ConfirmAlert>
    ),
  },
];

const useSnackbar = () => {
  const [state, setState] = useState({
    open: false,
    message: "",
    severity: "error",
  });

  const show = useCallback((message, severity = "error") => {
    setState({ open: true, message, severity });
  }, []);

  const hide = useCallback(() => {
    setState((prev) => ({ ...prev, open: false }));
  }, []);

  return { snackbar: state, show, hide };
};

const useApiHandler = (show) =>
  useCallback(
    (res, successMsg, errorMsg) => {
      if (res?.status === 200) {
        if (res?.data?.success) {
          show(successMsg, "success");
          return true;
        } else {
          show(res?.data?.message || errorMsg, "error");
          return false;
        }
      } else {
        show(MESSAGES.FETCH_UNKNOWN, "error");
        return false;
      }
    },
    [show],
  );

const isValidIP = (ip) =>
  /^(25[0-5]|2[0-4]\d|[01]?\d\d?)(\.(25[0-5]|2[0-4]\d|[01]?\d\d?)){3}$/.test(
    ip?.trim(),
  );

// ─────────────────────────────────────────────
const IpWhiteListPage = () => {
  const [ipAddress, setIPAddress] = useState("");
  const [ipAddressesList, setIpAddressesList] = useState([]);
  const [pending, setPending] = useState(true);

  const adminId = useSelector((state) => state.auth.userData?.adminId);
  const { snackbar, show, hide } = useSnackbar();
  const handleApiResponse = useApiHandler(show);

  useEffect(() => {
    getWhitelistedIP()
      .then((res) => {
        if (res?.status === 200) {
          if (res?.data?.success) {
            setIpAddressesList(res.data.data);
          } else {
            show(res?.data?.message || MESSAGES.FETCH_ERROR, "error");
          }
        } else {
          show(MESSAGES.FETCH_UNKNOWN, "error");
        }
      })
      .catch(() => show(MESSAGES.FETCH_UNKNOWN, "error"))
      .finally(() => {
        setPending(false);
      });

    return () => {};
  }, [ipAddress]); // intentionally run once

  const buildPayload = useCallback(
    (action) => ({
      ipAddress: ipAddress.trim(),
      action,
      createdBy: adminId,
      updatedBy: adminId,
    }),
    [ipAddress, adminId],
  );

  const handleDelete = useCallback(
    (item) => {
      const body = {
        ipAddress: item?.ipAddress,
        action: "remove",
        createdBy: adminId,
        updatedBy: adminId,
      };
      manageWhitelistIP(body).then((res) => {
        if (
          handleApiResponse(res, MESSAGES.DELETE_SUCCESS, MESSAGES.DELETE_ERROR)
        ) {
          setIpAddressesList((prev) =>
            prev.filter((ip) => ip.ipAddress !== item.ipAddress),
          );
        }
      });
    },
    [adminId, handleApiResponse],
  );

  const columns = React.useMemo(
    () => buildColumns(handleDelete),
    [handleDelete],
  );

  const handleSubmit = useCallback(() => {
    if (!ipAddress.trim()) return show(MESSAGES.IP_REQUIRED, "error");
    if (!isValidIP(ipAddress))
      return show("Enter a valid IPv4 address.", "error");

    manageWhitelistIP(buildPayload("add")).then((res) => {
      if (handleApiResponse(res, MESSAGES.ADD_SUCCESS, MESSAGES.ADD_ERROR)) {
        setIPAddress("");
      }
    });
  }, [ipAddress, buildPayload, handleApiResponse, show]);

  return (
    <>
      <AnimatedPage>
        <TopHeading showButton={false} heading="White List Your IP" />

        <Paper sx={{ px: 2, py: 4, mx: 2, mb: 1 }} elevation={2}>
          <Grid container sx={{ px: 2, py: 1 }} alignItems="center">
            <Grid item sx={{ mx: 1 }} xs={12} sm={6} md={6}>
              <TextField
                type="text"
                value={ipAddress}
                fullWidth
                label="IP Address"
                placeholder="e.g. 192.168.1.1"
                sx={{
                  "& .MuiOutlinedInput-notchedOutline": {
                    border: "2px solid #f0f0f0",
                  },
                }}
                onChange={(e) => setIPAddress(e.target.value)}
                variant="outlined"
              />
            </Grid>
            <Grid item sx={{ mx: 1 }} xs={12} sm={2} md={2}>
              <Button
                variant="contained"
                color="primary"
                fullWidth
                onClick={handleSubmit}
              >
                Submit
              </Button>
            </Grid>
          </Grid>
        </Paper>

        <Stack spacing={2} sx={{ mx: 4, my: 2 }}>
          <CustomDataTable
            columns={columns}
            data={ipAddressesList}
            pagination
            progressPending={pending}
            highlightOnHover
          />
        </Stack>
      </AnimatedPage>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={SNACKBAR_DURATION}
        anchorOrigin={SNACKBAR_POSITION}
        onClose={hide}
      >
        <Alert
          severity={
            snackbar.severity === "success" ? undefined : snackbar.severity
          }
          onClose={hide}
          sx={{
            width: "100%",
            ...(snackbar.severity === "success" && {
              backgroundColor: "#2196f3",
              color: "#fff",
            }),
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export const IpWhiteList = Hoc(IpWhiteListPage, { name: "" });
